// Utils for calculating width of funnels charts
import range from 'lodash/range';

import {
  SelectedColumns,
  TableMetrics,
  TableNonToggleableMetrics,
  TableToggleableMetrics,
} from 'common/report/models/funnels/types';
import {DEFAULT_FUNNELS_CHART_SIZING} from 'common/widgets/funnels-chart';
import {OVERVIEW_PANEL_WIDTH} from 'common/report/widgets/funnels/overview-panel';
import {FUNNELS_CHECKBOX_CELL_WIDTH} from 'common/components/data-table';

export const GRID_GAP = 26;
const FIRST_COLUMN_WIDTH = OVERVIEW_PANEL_WIDTH + DEFAULT_FUNNELS_CHART_SIZING.yAxisContainerWidth;

/**
 * exported for testing
 * we *SHOULD* not be using this outside this file.
 */
export type TableWidthConfig = {
  columnMinWidth: number;
  columnMaxWidth: number;
  segmentedColumnMinWidth: number;
  segmentedColumnMaxWidth: number;
  gridGap: number;
  firstStepMetricsOrder: TableMetrics[];
  otherStepMetricsOrder: TableMetrics[];
  firstColumnMetricsOrder: TableMetrics[];
  firstColumnWidth: number;
  metricsMinWidth: {[key in TableMetrics]: number};
};

const defaultSegmentColWidth = 145;
const DEFAULT_CHART_COL_MIN_WIDTH: {[key in TableMetrics]: number} = {
  [TableToggleableMetrics.StatSig]: 60,
  [TableToggleableMetrics.ConvPrevStep]: 60,
  [TableToggleableMetrics.ConvFirstStep]: 60,
  [TableToggleableMetrics.TimePrevStep]: 52,
  [TableToggleableMetrics.TimeFirstStep]: 52,
  [TableToggleableMetrics.Count]: 80,
  [TableNonToggleableMetrics.Segment]: defaultSegmentColWidth,
  [TableNonToggleableMetrics.OverallConv]: FIRST_COLUMN_WIDTH - defaultSegmentColWidth - FUNNELS_CHECKBOX_CELL_WIDTH,
  [TableNonToggleableMetrics.NoMetrics]: 80,
};

const DEFAULT_TABLE_WIDTH_CONFIG: TableWidthConfig = {
  columnMinWidth: 80,
  columnMaxWidth: 480,
  segmentedColumnMinWidth: 195,
  segmentedColumnMaxWidth: 480,
  gridGap: 26,
  firstStepMetricsOrder: [TableToggleableMetrics.StatSig, TableToggleableMetrics.Count],
  otherStepMetricsOrder: [
    TableToggleableMetrics.TimePrevStep,
    TableToggleableMetrics.TimeFirstStep,
    TableToggleableMetrics.ConvPrevStep,
    TableToggleableMetrics.ConvFirstStep,
    TableToggleableMetrics.Count,
  ],
  firstColumnMetricsOrder: [TableNonToggleableMetrics.Segment, TableNonToggleableMetrics.OverallConv],
  firstColumnWidth: FIRST_COLUMN_WIDTH,
  metricsMinWidth: DEFAULT_CHART_COL_MIN_WIDTH,
};

/**
 * Returns a width to use for the viewport when taking a screenshot
 *
 * @param numSteps The number of steps in the funnel
 * @param minSteps The min number of steps to incude in screenshot (overrides maxWidth)
 * @param selectedColumns The metrics/columns selected to be displayed
 * @param shouldShowStatSig If we should show stat sig (some breakdowns don't support stat sig)
 * @param numSegments The number of breakdowns for this report
 * @param minWidth The min width of a screenshot (for less steps, we snap to this)
 * @param maxWidth The max width of a screenshot (mostly a guide to include more steps, does not override minSteps)
 * @param tableWidthConfig set *ONLY* in tests, the config for determining width
 */
export function getOverallWidthForScreenshot(
  numSteps: number,
  minSteps: number,
  selectedColumns: SelectedColumns,
  shouldShowStatSig: boolean,
  numSegments: number,
  minWidth: number,
  maxWidth: number,
  tableWidthConfig: TableWidthConfig = DEFAULT_TABLE_WIDTH_CONFIG,
) {
  const firstColWidth = tableWidthConfig.firstColumnWidth;

  // Get step width based on numSteps and minWidth
  // This is guaranteed to be the smallest width for a step
  const stepWidth =
    getStepWidth(selectedColumns, shouldShowStatSig, numSegments, numSteps, minWidth, tableWidthConfig) +
    tableWidthConfig.gridGap;

  // Get num steps based on the max width (i.e. fit as many steps as possible before max width)
  // do not go lower than min steps.
  const numPossibleStepsWithinMaxWidth = Math.max(minSteps, Math.floor((maxWidth - firstColWidth) / stepWidth));
  const numStepsIncluded = Math.min(numPossibleStepsWithinMaxWidth, numSteps);

  const widthForNumSteps = firstColWidth + numStepsIncluded * stepWidth;

  return Math.max(widthForNumSteps, minWidth);
}

/**
 * Returns the width of any single step in the funnel
 *
 * @param selectedColumns The metrics/columns selected to be displayed
 * @param shouldShowStatSig If we should show stat sig (some breakdowns don't support stat sig)
 * @param numSegments The number of breakdowns for this report
 * @param numSteps The number of steps in the funnel
 * @param tableWidth The width of the container for the table
 * @param tableWidthConfig set *ONLY* in tests, the config for determining width
 */
export function getStepWidth(
  selectedColumns: SelectedColumns,
  shouldShowStatSig: boolean,
  numSegments: number,
  numSteps: number,
  tableWidth: number,
  tableWidthConfig: TableWidthConfig = DEFAULT_TABLE_WIDTH_CONFIG,
): number {
  const minWidth = numSegments > 0 ? tableWidthConfig.segmentedColumnMinWidth : tableWidthConfig.columnMinWidth;
  const minWidthFirstStep: number = getMinWidthForStep(
    tableWidthConfig,
    selectedColumns,
    getFirstStepMetrics(tableWidthConfig, numSegments > 0 && shouldShowStatSig),
  );
  const minWidthOtherSteps: number = getMinWidthForStep(
    tableWidthConfig,
    selectedColumns,
    tableWidthConfig.otherStepMetricsOrder,
  );

  const minStepWidth = Math.max(minWidthFirstStep, minWidthOtherSteps, minWidth);

  let spareSpace =
    tableWidth - (minStepWidth + tableWidthConfig.gridGap) * numSteps - tableWidthConfig.firstColumnWidth;
  if (numSegments > 0) {
    // If segmented, do not fill to the end since it may cause
    // horizontal scrolling due to the vertical scroll bar showing up.
    spareSpace -= 25;
  }
  if (spareSpace > 0) {
    const maxExpandedWidth = minStepWidth + Math.floor(spareSpace / numSteps);
    return Math.min(maxExpandedWidth, getMaxWidthForStep(tableWidthConfig, numSegments));
  } else {
    return minStepWidth;
  }
}

/** A TableColumnsSpec is a list of column identifiers and associated width. */
export type TableColumnsSpec = TableColumnSpec[][];
export type TableColumnSpec = {
  identifier: TableMetrics;
  width: number;
  stepIdx?: number;
};

/**
 * Returns a TableColumnsSpec (list of column + sub columns with identifiers and associated width)
 *
 * @param selectedColumns The metrics/columns selected to be displayed
 * @param numSegments The number of breakdowns for this report
 * @param numSteps The number of steps in the funnel
 * @param shouldShowStatSig If we should show stat sig (some breakdowns don't support stat sig)
 * @param tableWidth The width of the container for the table
 * @param tableWidthConfig set *ONLY* in tests, the config for determining width
 */
export function getTableColumnSpec(
  selectedColumns: SelectedColumns,
  numSegments: number,
  numSteps: number,
  shouldShowStatSig: boolean,
  tableWidth: number,
  tableWidthConfig: TableWidthConfig = DEFAULT_TABLE_WIDTH_CONFIG,
): TableColumnsSpec {
  const firstGroup: TableColumnSpec[] = [
    {
      identifier: TableNonToggleableMetrics.Segment,
      width:
        tableWidthConfig.metricsMinWidth[TableNonToggleableMetrics.Segment] +
        (numSegments > 0 ? 0 : FUNNELS_CHECKBOX_CELL_WIDTH),
    },
    {
      identifier: TableNonToggleableMetrics.OverallConv,
      width: tableWidthConfig.metricsMinWidth[TableNonToggleableMetrics.OverallConv],
    },
  ];
  let columns = [firstGroup];

  for (const stepIdx of range(numSteps)) {
    let metrics = tableWidthConfig.otherStepMetricsOrder;
    if (stepIdx === 0) {
      metrics = getFirstStepMetrics(tableWidthConfig, numSegments > 0 && shouldShowStatSig);
    }

    let filteredMetrics = metrics.filter((metric) => selectedColumns[metric]);
    if (filteredMetrics.length === 0) {
      filteredMetrics = [TableNonToggleableMetrics.NoMetrics];
    }

    const filtered: TableColumnSpec[] = filteredMetrics.map((metric) => {
      return {
        identifier: metric,
        width: tableWidthConfig.metricsMinWidth[metric],
        stepIdx,
      };
    });

    // Adjust columns without enough width by evenly distributing
    // excess width amongst the sub groups.
    const groupWidth = getStepWidth(
      selectedColumns,
      shouldShowStatSig,
      numSegments,
      numSteps,
      tableWidth,
      tableWidthConfig,
    );
    const subGroupMinWidthSum = filtered.reduce((a, i) => a + i.width, 0);
    if (groupWidth > subGroupMinWidthSum) {
      const totalWidthDiff = groupWidth - subGroupMinWidthSum;
      const widthToAddToEachRow = Math.floor((groupWidth - subGroupMinWidthSum) / filtered.length);
      filtered.forEach((item) => (item.width += widthToAddToEachRow));
      filtered[filtered.length - 1].width += totalWidthDiff - widthToAddToEachRow * filtered.length;
    }

    // Add column grid gap to width.
    filtered[0].width += tableWidthConfig.gridGap / 2;
    filtered[filtered.length - 1].width += tableWidthConfig.gridGap / 2;

    columns = [...columns, filtered];
  }

  return columns;
}

function getFirstStepMetrics(tableWidthConfig: TableWidthConfig, shouldShowStatSig: boolean): TableMetrics[] {
  if (!shouldShowStatSig) {
    return tableWidthConfig.firstStepMetricsOrder.filter((item) => item !== TableToggleableMetrics.StatSig);
  }
  return tableWidthConfig.firstStepMetricsOrder;
}

function getMinWidthForStep(
  tableWidthConfig: TableWidthConfig,
  selectedColumns: SelectedColumns,
  metricsOrder: TableMetrics[],
): number {
  return Object.keys(selectedColumns)
    .filter((subGroup) => metricsOrder.findIndex((item) => item === subGroup) !== -1 && selectedColumns[subGroup])
    .reduce((a, i) => a + tableWidthConfig.metricsMinWidth[i], 0);
}

function getMaxWidthForStep(tableWidthConfig: TableWidthConfig, numSegments: number) {
  if (numSegments > 0) {
    return tableWidthConfig.segmentedColumnMaxWidth;
  } else {
    return tableWidthConfig.columnMaxWidth;
  }
}
